import { apiClient, ApiError } from '@/lib/apiClient';
import type {
  ClarifyDashboardData,
  ClarifyListResponse,
  ClarifyListView,
  ClarifyRequestDetail,
  ClarifyWorkspaceMember,
  CreateClarifyRequestInput,
  RejectClarifyRequestInput,
  SuggestClarifyDraftInput,
  SuggestClarifyDraftResponse,
  ReviewClarifyRequestInput,
  SuggestClarifyTitleInput,
  SuggestClarifyTitleResponse,
  TriageClarifyRequestInput,
} from '@/types/clarify';

function buildListUrl(workspaceId: string, view: ClarifyListView) {
  const params = new URLSearchParams({ view, page: '1', pageSize: '50' });
  return `/api/dotnet/workspaces/${workspaceId}/knowledge-requests?${params.toString()}`;
}

export const clarifyApi = {
  getDashboard(workspaceId: string, token?: string | null) {
    return apiClient.get<ClarifyDashboardData>(
      `/api/dotnet/workspaces/${workspaceId}/knowledge-requests/dashboard`,
      { token },
    );
  },

  getList(workspaceId: string, view: ClarifyListView, token?: string | null) {
    return apiClient.get<ClarifyListResponse>(buildListUrl(workspaceId, view), { token });
  },

  getDetail(workspaceId: string, requestId: string, token?: string | null) {
    return apiClient.get<ClarifyRequestDetail>(
      `/api/dotnet/workspaces/${workspaceId}/knowledge-requests/${requestId}`,
      { token },
    );
  },

  createRequest(workspaceId: string, input: CreateClarifyRequestInput, token?: string | null) {
    return apiClient.post<{ id: string; requestNumber: string }>(
      `/api/dotnet/workspaces/${workspaceId}/knowledge-requests`,
      { token, body: input },
    );
  },

  suggestTitle(input: SuggestClarifyTitleInput, token?: string | null) {
    return apiClient.post<SuggestClarifyTitleResponse>(
      '/api/python/clarify/suggest-title',
      { token, body: input },
    );
  },

  suggestDraft(input: SuggestClarifyDraftInput, token?: string | null) {
    return apiClient.post<SuggestClarifyDraftResponse>(
      '/api/python/clarify/suggest-draft',
      { token, body: input },
    );
  },

  triageRequest(workspaceId: string, requestId: string, input: TriageClarifyRequestInput, token?: string | null) {
    return apiClient.post<{ id: string; interviewTaskNumber?: string | null }>(
      `/api/dotnet/workspaces/${workspaceId}/knowledge-requests/${requestId}/triage`,
      { token, body: input },
    );
  },

  rejectRequest(workspaceId: string, requestId: string, input: RejectClarifyRequestInput, token?: string | null) {
    return apiClient.post<{ id: string; detailStatus: string }>(
      `/api/dotnet/workspaces/${workspaceId}/knowledge-requests/${requestId}/reject`,
      { token, body: input },
    );
  },

  linkInterviewSession(workspaceId: string, requestId: string, sessionId: string, token?: string | null) {
    return apiClient.post<{ id: string; interviewSessionId?: string | null }>(
      `/api/dotnet/workspaces/${workspaceId}/knowledge-requests/${requestId}/interview-session`,
      { token, body: { sessionId } },
    );
  },

  submitForReview(workspaceId: string, requestId: string, note?: string, token?: string | null) {
    return apiClient.post<{ id: string; detailStatus: string }>(
      `/api/dotnet/workspaces/${workspaceId}/knowledge-requests/${requestId}/submit`,
      { token, body: { note } },
    );
  },

  reviewRequest(workspaceId: string, requestId: string, input: ReviewClarifyRequestInput, token?: string | null) {
    return apiClient.post<{ id: string; detailStatus: string }>(
      `/api/dotnet/workspaces/${workspaceId}/knowledge-requests/${requestId}/review`,
      { token, body: input },
    );
  },

  getAssignableMembers(workspaceId: string, token?: string | null) {
    return apiClient.get<ClarifyWorkspaceMember[]>(
      `/api/dotnet/workspaces/${workspaceId}/members`,
      { token },
    );
  },
};

export async function getOptionalClarifyList(
  workspaceId: string,
  view: ClarifyListView,
  token?: string | null,
): Promise<ClarifyListResponse> {
  try {
    return await clarifyApi.getList(workspaceId, view, token);
  } catch (error) {
    if (error instanceof ApiError && (error.status === 403 || error.status === 404)) {
      return { items: [], totalCount: 0, page: 1, pageSize: 50, totalPages: 0 };
    }
    throw error;
  }
}

export async function getOptionalAssignableMembers(
  workspaceId: string,
  token?: string | null,
): Promise<ClarifyWorkspaceMember[]> {
  try {
    return await clarifyApi.getAssignableMembers(workspaceId, token);
  } catch (error) {
    if (error instanceof ApiError && (error.status === 403 || error.status === 404)) {
      return [];
    }
    throw error;
  }
}